import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

/**
 * Renders the Propract inline booking widget.
 *
 * Equivalent HTML:
 *   <div id="propract-book"></div>
 *   <script
 *     src="https://propract.com/widget.js"
 *     data-event="..."
 *     data-expert="..."
 *     data-mode="inline"
 *     data-target="#propract-book"
 *     async></script>
 *
 * We inject the script programmatically because React does not execute
 * <script> tags rendered via innerHTML/dangerouslySetInnerHTML.
 */
export default function PropractEmbed({
  src = "https://propract.com/widget.js",
  event = "evt_92df8367dff6",
  expert = "pp_expert_2688666a6a2b",
  mode = "inline",
  containerId = "propract-book",
  testid = "propract-embed",
}) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // 'loading' | 'ready' | 'error'

  useEffect(() => {
    // If the widget script is already on the page, just trigger re-render
    // by inserting a fresh script tag scoped to this container.
    const existing = document.querySelector(
      `script[data-propract-target="#${containerId}"]`
    );
    if (existing) {
      existing.remove();
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.setAttribute("data-event", event);
    script.setAttribute("data-expert", expert);
    script.setAttribute("data-mode", mode);
    script.setAttribute("data-target", `#${containerId}`);
    script.setAttribute("data-propract-target", `#${containerId}`);

    script.onload = () => setStatus("ready");
    script.onerror = () => setStatus("error");

    document.body.appendChild(script);

    return () => {
      // Cleanup script tag and any markup the widget injected into our container
      script.remove();
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [src, event, expert, mode, containerId]);

  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-paper border border-[rgba(74,63,57,0.10)] p-2 sm:p-4 min-h-[420px] relative"
    >
      {/* Loading skeleton — shown until the widget mounts / replaces this UI */}
      {status === "loading" && (
        <div
          aria-hidden="true"
          className="absolute inset-0 flex flex-col items-center justify-center text-ink-muted gap-3 pointer-events-none"
        >
          <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
          <p className="text-sm">Loading booking widget…</p>
        </div>
      )}

      {status === "error" && (
        <div
          role="alert"
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-3"
        >
          <p className="font-display text-lg text-ink">
            We couldn’t load the booking widget.
          </p>
          <p className="text-sm text-ink-muted max-w-md">
            Please refresh the page, or reach out to us on WhatsApp and we’ll
            help you enroll directly.
          </p>
        </div>
      )}

      {/* Target container the Propract widget renders into */}
      <div
        id={containerId}
        ref={containerRef}
        data-testid="propract-book-container"
      />
    </div>
  );
}
